import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Container,
  Chip,
  useTheme,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, FormatQuote, NavigateBefore, NavigateNext } from '@mui/icons-material';
import recommendationsData from '../data/recommendations.json';
import quotesData from '../data/quotes.json';

interface Quote {
  quote: string;
  author: string;
}

const RecommendationsSection: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentQuote, setCurrentQuote] = useState<Quote>(quotesData[0]);

  // Auto-rotate every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => {
        const nextIndex = prevIndex + 2;
        return nextIndex >= recommendationsData.length ? 0 : nextIndex;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Change quote every 8 seconds
  useEffect(() => {
    const quoteInterval = setInterval(() => {
      const randomIndex = Math.floor(Math.random() * quotesData.length);
      setCurrentQuote(quotesData[randomIndex]);
    }, 8000);

    return () => clearInterval(quoteInterval);
  }, []);

  const handleNext = () => {
    setCurrentIndex((prevIndex) => {
      const nextIndex = prevIndex + 2;
      return nextIndex >= recommendationsData.length ? 0 : nextIndex;
    });
  };

  const handlePrevious = () => {
    setCurrentIndex((prevIndex) => {
      const prevIndexNew = prevIndex - 2;
      return prevIndexNew < 0 ? Math.max(0, recommendationsData.length - 2) : prevIndexNew;
    });
  };

  const cardVariants = {
    enter: {
      opacity: 0,
      x: 100,
    },
    center: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut" as const,
      },
    },
    exit: {
      opacity: 0,
      x: -100,
      transition: {
        duration: 0.3,
        ease: "easeIn" as const,
      },
    },
  };

  const cardBg = isDark
    ? 'linear-gradient(135deg, rgba(26,26,34,0.98) 0%, rgba(34,34,44,0.98) 100%)'
    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%)';
  const cardBorder = isDark
    ? '1px solid rgba(144,202,249,0.08)'
    : '1px solid rgba(26, 115, 232, 0.08)';

  // Get current two recommendations
  const currentRecommendations = [
    recommendationsData[currentIndex],
    recommendationsData[currentIndex + 1] || recommendationsData[0]
  ];

  return (
    <Box
      id="recommendations"
      sx={{
        py: { xs: 4, sm: 6, md: 8 },
        backgroundColor: theme.palette.background.default,
        minHeight: { xs: 'auto', md: '100vh' },
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Box textAlign="center" mb={{ xs: 4, sm: 5, md: 6 }}>
            <Typography
              variant={isMobile ? 'h3' : 'h2'}
              component="h2"
              align="center"
              className="section-heading-allura"
              sx={{ 
                mb: { xs: 4, sm: 6, md: 8 }, 
                fontWeight: 400,
                fontFamily: 'Allura, cursive !important',
                fontSize: { xs: '36px !important', sm: '42px !important', md: '48px !important' },
                background: theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 50%, #EC4899 100%)'
                  : 'linear-gradient(135deg, #1E40AF 0%, #7C3AED 50%, #DB2777 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                '@media (min-width: 480px) and (max-width: 600px)': {
                  fontSize: '48px !important',
                },
                '@media (min-width: 601px) and (max-width: 960px)': {
                  fontSize: '56px !important',
                },
                '@media (min-width: 961px) and (max-width: 1280px)': {
                  fontSize: '68px !important',
                },
                '@media (min-width: 1281px)': {
                  fontSize: '72px !important',
                },
              }}
            >
              Recommendations
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              className="section-description-allura"
              sx={{ 
                maxWidth: 600, 
                mx: 'auto',
                fontFamily: 'Allura, cursive !important',
                fontWeight: 400,
                fontSize: '28px !important',
                '@media (min-width: 480px) and (max-width: 600px)': {
                  fontSize: '32px !important',
                },
                '@media (min-width: 601px) and (max-width: 960px)': {
                  fontSize: '34px !important',
                },
                '@media (min-width: 961px) and (max-width: 1280px)': {
                  fontSize: '35px !important',
                },
                '@media (min-width: 1281px)': {
                  fontSize: '36px !important',
                },
              }}
            >
              What colleagues and mentors say about working with me
            </Typography>
          </Box>
        </motion.div>

        <Box sx={{ position: 'relative', maxWidth: 800, mx: 'auto' }}>
          {/* Navigation Buttons */}
          <IconButton
            onClick={handlePrevious}
            sx={{
              position: 'absolute',
              left: -60,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.4)' : theme.palette.background.paper,
              color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
              border: theme.palette.mode === 'dark' ? '1px solid rgba(0, 0, 0, 0.5)' : 'none',
              boxShadow: theme.palette.mode === 'dark' ? '0 4px 20px rgba(0, 0, 0, 0.5)' : theme.shadows[4],
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.6)' : theme.palette.action.hover,
                border: theme.palette.mode === 'dark' ? '1px solid rgba(0, 0, 0, 0.7)' : 'none',
              },
              '@media (max-width: 900px)': {
                left: 10,
                top: 'auto',
                bottom: -60,
                transform: 'none',
              },
            }}
          >
            <NavigateBefore />
          </IconButton>

          <IconButton
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: -60,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.4)' : theme.palette.background.paper,
              color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
              border: theme.palette.mode === 'dark' ? '1px solid rgba(0, 0, 0, 0.5)' : 'none',
              boxShadow: theme.palette.mode === 'dark' ? '0 4px 20px rgba(0, 0, 0, 0.5)' : theme.shadows[4],
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.6)' : theme.palette.action.hover,
                border: theme.palette.mode === 'dark' ? '1px solid rgba(0, 0, 0, 0.7)' : 'none',
              },
              '@media (max-width: 900px)': {
                right: 10,
                top: 'auto',
                bottom: -60,
                transform: 'none',
              },
            }}
          >
            <NavigateNext />
          </IconButton>

          {/* Carousel Content */}
          <Box sx={{ position: 'relative', minHeight: { xs: 250, md: 300 }, display: 'flex', gap: { xs: 2, sm: 3, md: 4 }, justifyContent: 'center', flexWrap: { xs: 'wrap', md: 'nowrap' }, flexDirection: { xs: 'column', md: 'row' }, alignItems: 'stretch', width: '100%' }}>
            {currentRecommendations.map((recommendation, cardIndex) => {
              // Calculate the actual recommendation index
              const actualIndex = currentIndex + cardIndex;
              const totalRecommendations = recommendationsData.length;
              return (
                <AnimatePresence key={actualIndex} mode="wait">
                  <motion.div
                    key={currentIndex + '-' + cardIndex}
                    variants={cardVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    style={{ width: '100%', maxWidth: 440, minWidth: 320, margin: '0 auto' }}
                  >
                    <Card
                      sx={{
                        width: { xs: '100%', md: 400 },
                        minWidth: { xs: '90vw', sm: 320, md: 360 },
                        maxWidth: { xs: '100%', md: 440 },
                        mx: { xs: 'auto', md: 0 },
                        my: { xs: 2, md: 0 },
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        overflow: 'visible',
                        background: cardBg,
                        border: cardBorder,
                        boxShadow: theme.shadows[4],
                        borderRadius: 3,
                        p: { xs: 2, md: 3 },
                        minHeight: { xs: 260, md: 340 },
                      }}
                    >
                      {/* Elegant Number Indicator */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: { xs: 8, md: 16 },
                          right: { xs: 8, md: 16 },
                          zIndex: 2,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.disabled,
                            fontWeight: 'bold',
                            fontFamily: '"Roboto Mono", "Courier New", monospace',
                            fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' },
                          }}
                        >
                          {actualIndex + 1} / {totalRecommendations}
                        </Typography>
                      </Box>

                      <CardContent
                        sx={{
                          pt: { xs: 2, md: 3 },
                          pb: { xs: 2, md: 2.5 },
                          px: { xs: 2, md: 2.5 },
                          flexGrow: 1,
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, md: 1.5 } }}>
                          <FormatQuote
                            sx={{
                              color: theme.palette.primary.main,
                              fontSize: { xs: 18, md: 20 },
                              mr: { xs: 0.75, md: 1 },
                            }}
                          />
                          <Box sx={{ display: 'flex', gap: 0.25 }}>
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                sx={{
                                  color: theme.palette.warning.main,
                                  fontSize: { xs: 12, md: 14 },
                                }}
                              />
                            ))}
                          </Box>
                        </Box>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: { xs: 2, md: 2.5 },
                            flexGrow: 1,
                            lineHeight: { xs: 1.4, md: 1.5 },
                            fontStyle: 'italic',
                            fontFamily: '"Roboto Mono", "Courier New", monospace',
                            fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.95rem' },
                            minHeight: { xs: 60, md: 80 },
                            display: '-webkit-box',
                            WebkitLineClamp: { xs: 3, md: 4 },
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          "{recommendation.text}"
                        </Typography>

                        <Box sx={{ mt: 'auto' }}>
                          <Typography
                            variant="subtitle1"
                            component="h3"
                            sx={{
                              fontWeight: 600,
                              color: theme.palette.text.primary,
                              mb: { xs: 0.25, md: 0.5 },
                              fontFamily: '"Roboto Mono", "Courier New", monospace',
                              fontSize: { xs: '0.875rem', sm: '0.9rem', md: '0.95rem' },
                            }}
                          >
                            {recommendation.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mb: { xs: 0.75, md: 1 },
                              lineHeight: { xs: 1.25, md: 1.3 },
                              fontFamily: '"Roboto Mono", "Courier New", monospace',
                              fontSize: { xs: '0.75rem', sm: '0.775rem', md: '0.8rem' },
                            }}
                          >
                            {recommendation.position}
                          </Typography>
                          <Chip
                            label={recommendation.date_context}
                            size="small"
                            sx={{
                              backgroundColor: `${theme.palette.primary.main}15`,
                              color: theme.palette.primary.main,
                              fontSize: { xs: '0.625rem', sm: '0.65rem', md: '0.7rem' },
                              height: { xs: 18, md: 20 },
                              fontWeight: 500,
                              fontFamily: '"Roboto Mono", "Courier New", monospace',
                            }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                </AnimatePresence>
              );
            })}
          </Box>

          {/* Dots Indicator */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, gap: 1 }}>
            {Array.from({ length: Math.ceil(recommendationsData.length / 2) }, (_, index) => (
              <Box
                key={index}
                onClick={() => setCurrentIndex(index * 2)}
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: Math.floor(currentIndex / 2) === index 
                    ? theme.palette.primary.main 
                    : theme.palette.grey[400],
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.light,
                  },
                }}
              />
            ))}
          </Box>
        </Box>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <Box textAlign="center" mt={6}>
            <motion.div
              key={currentQuote.quote}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ fontStyle: 'italic', fontFamily: '"Roboto Mono", "Courier New", monospace' }}
              >
                "{currentQuote.quote}" - {currentQuote.author}
              </Typography>
            </motion.div>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default RecommendationsSection; 